import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingBox from '../../../components/LoadingBox';
import { PlanProvider, SizeCalendar } from '../../../contexts/PlanOperationContext/Plan';
import { selectOneSetting, selectOverview, selectPermissions } from '../../../util/store/selectors';
import { aWeekMillisecond } from '../../../util/varibles/constants';
import { getKeyWeek } from "../../../util/varibles/global";
import { ACTION } from '../../../util/varibles/permission';
import { OverviewSetting } from '../../../util/varibles/userSetting';
import { CALENDAR_ID } from "../constants";
import { overviewActions } from '../reducer';
import useTimelineZoom from '../useTimelineZoom';
import Header from "./Header";
import styles from "./style.module.scss";
import Vessels from './Vessels';

const Loading: React.FC = () => {
    const weeks = useSelector(selectOverview.weeks);
    const loadingData = useSelector(selectOverview.loadingData);
    const loadingLazy = useSelector(selectOverview.loadingLazy);
    const beforeCount = useSelector(selectOverview.beforeCount);
    const afterCount = useSelector(selectOverview.afterCount);
    const wellboats = useSelector(selectOverview.wellboats);

    const dispatch = useDispatch();

    const [mode, setMode] = useState('');
    const scrollAnchor = useRef<{ weekKey: string, offset: number } | null>(null);

    const onScroll = useCallback((e: Event) => {
        const target = e.currentTarget as HTMLDivElement | null;
        if (loadingLazy || !target || weeks.length === 0)
            return;
        const { scrollLeft, scrollWidth, clientWidth } = target;
        if (scrollLeft <= 1) {
            const { key: weekKey, time: finishTime } = weeks[0];
            const startTime = finishTime - (3 * aWeekMillisecond);
            let newWeeks = [...weeks];
            for (let i = 1; i <= 3; i++) {
                const time = finishTime - (i * aWeekMillisecond);
                const keyWeek = getKeyWeek(time);
                newWeeks = [{ key: keyWeek, time }, ...newWeeks]
            }
            const weekElement = document.getElementById(weekKey);
            if (weekElement) {
                scrollAnchor.current = {
                    weekKey,
                    offset: weekElement.offsetLeft - scrollLeft,
                };
            }
            dispatch(overviewActions.lazyLoadingRequest({
                isLoad: beforeCount || wellboats.before_count,
                key: 'beforeCount',
                weeks: newWeeks,
                startTime,
                finishTime: finishTime - 1
            }));
            setMode('left');
        } else if (scrollLeft + clientWidth >= scrollWidth - 1) {
            const { time: lastWeekTime } = weeks[weeks.length - 1];
            const startTime = lastWeekTime + aWeekMillisecond;
            const finishTime = startTime + (3 * aWeekMillisecond) - 1;
            let newWeeks = [...weeks];
            for (let i = 1; i <= 3; i++) {
                const time = lastWeekTime + (i * aWeekMillisecond);
                const keyWeek = getKeyWeek(time);
                newWeeks = [...newWeeks, { key: keyWeek, time }]
            }
            dispatch(overviewActions.lazyLoadingRequest({
                isLoad: afterCount || wellboats.after_count,
                key: 'afterCount',
                weeks: newWeeks,
                startTime,
                finishTime
            }));
            setMode('right');
        }
    }, [loadingLazy, weeks, dispatch, beforeCount, afterCount, wellboats.before_count, wellboats.after_count]);

    useLayoutEffect(() => {
        const anchor = scrollAnchor.current;
        if (mode !== 'left' || !anchor || weeks[0]?.key === anchor.weekKey)
            return;

        const calendar = document.getElementById(CALENDAR_ID);
        const weekElement = document.getElementById(anchor.weekKey);
        if (calendar && weekElement) {
            const nextScrollLeft = weekElement.offsetLeft - anchor.offset;
            if (Math.abs(calendar.scrollLeft - nextScrollLeft) > 1)
                calendar.scrollLeft = nextScrollLeft;
            scrollAnchor.current = null;
        }
    }, [mode, weeks]);

    useEffect(() => {
        if (!loadingLazy)
            scrollAnchor.current = null;
    }, [loadingLazy]);

    useEffect(() => {
        const el = document.getElementById(CALENDAR_ID);
        if (!el)
            return;

        el.addEventListener('scroll', onScroll);
        return () => el.removeEventListener('scroll', onScroll);
    }, [onScroll]);


    return <>
        {(loadingLazy && mode === 'left') && <div className={styles.loading} data-position='left'>
            <LoadingBox loading={true} />
        </div>}
        {loadingData && <div className={styles.loading} data-position='center'><LoadingBox loading={true} /></div>}
        {(loadingLazy && mode === 'right') && <div className={styles.loading} data-position='right'>
            <LoadingBox loading={true} />
        </div>}
    </>;
};

const Calendar: React.FC = () => {
    const calendarRef = useRef<HTMLDivElement>(null);
    useTimelineZoom(calendarRef);
    const weekPerPage = useSelector(selectOneSetting(OverviewSetting.WEEK_PER_PAGE));
    const [canUseVesselMenu, canUseOperationMenu, canUpdateOperation] = useSelector(selectPermissions([
        ACTION.OVERVIEW.VESSEL_MENU,
        ACTION.OVERVIEW.OP_MENU,
        ACTION.OVERVIEW.UPDATE_OP,
    ]));

    useEffect(() => {
        setTimeout(() => {
            const week = getKeyWeek(Date.now())
            const element = document.getElementById(week);
            if (element) {
                const { offsetLeft } = element;
                const calendar = document.getElementById(CALENDAR_ID);
                if (calendar)
                    calendar.scrollLeft = offsetLeft - 162;
            }
        }, 100)
    }, []);

    return <div className={styles.calendar}>
        <div className={styles.screen}>
            <Header />
            <PlanProvider
                calendarRef={() => calendarRef.current as HTMLDivElement}
                permission={{
                    VESSEL_MENU: canUseVesselMenu,
                    OP_MENU: canUseOperationMenu,
                    UPDATE_OP: canUpdateOperation,
                }}
            >
                <SizeCalendar getWeek={state => state.overview.weeks} />
                <div
                    id={CALENDAR_ID}
                    ref={calendarRef}
                    className={styles.schedule}
                    style={{ '--week-per-screen': weekPerPage } as React.CSSProperties}
                >
                    <Vessels calendarRef={calendarRef} />
                    <Loading />
                </div>
            </PlanProvider>
        </div>
    </div>
};

export default Calendar;
